import React from "react";
import { Loader, Select } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { DatePickerInput } from "@mantine/dates";
import "@mantine/dates/styles.css";
import "./WelcomeSurveySummary.css";

// Sentinel used for the "all …" option in each dropdown. Mantine needs a real
// string value, and the request sends null for it.
export const ALL_OPTION = "all";

// The "needs your attention" queue, built from the report-data counts. Each
// count is subscriptions in the window, matching how the endpoint splits them.
function attentionItems(data) {
  return [
    {
      count: formatCount(data?.survey_never_sent_count),
      title: "Surveys never sent",
      note: "No survey sent for the subscription",
    },
    {
      count: formatCount(data?.survey_incompleted_count),
      title: "Sent, no reply yet",
      note: "Sent, never completed by the customer",
    },
    // Rep-device completions need device data the response doesn't carry yet.
    // {
    //   count: 2,
    //   title: "Possible rep-device completions",
    //   note: "Same device fingerprint as the rep",
    // },
  ];
}

// The date picker sits where the "Welcome Call Setup" button was, so it wears
// that button's clothes: outlined accent, and the sans it used rather than the
// uppercase mono the neutral filters take.
export const accentFilterStyles = {
  input: {
    // Font and size are left alone deliberately: the theme sets no fontFamily,
    // so the button here took Mantine's own default, and the input takes the
    // same one. Naming a stack would be a guess at what that resolves to.
    fontWeight: "var(--font-weight-semibold)",
    color: "var(--color-primary)",
    borderColor: "var(--color-primary)",
  },
  section: {
    color: "var(--color-primary)",
  },
};

export const filterSelectStyles = {
  input: {
    fontFamily: "var(--label-font-family)",
    fontSize: "var(--font-size-xs)",
    letterSpacing: "var(--label-letter-spacing)",
    textTransform: "uppercase",
    color: "var(--color-text-primary)",
    borderColor: "var(--color-border-extra-light)",
  },
};

// Round a number to a whole percent, or to two places when it isn't whole, and
// leave a missing figure as a dash.
function formatPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  const rounded = Math.round(number * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(2)}%`;
}

function formatCount(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString() : "—";
}

/**
 * The report's own filters: the office dropdown and the reporting window. Both
 * narrow the report-data request, so they move the summary figures as well as
 * the table. The parent owns the values.
 */
export function WelcomeSurveyFilters({
  filters,
  onChange,
  offices = [],
  // Subscription dropdown is commented out for now.
  // subscriptionIds = [],
}) {
  const officeOptions = [
    { value: ALL_OPTION, label: "All offices" },
    ...offices.map(office => ({
      value: String(office.id),
      label: office.name || `Office ${office.fr_office_id ?? office.id}`,
    })),
  ];

  // const subscriptionOptions = [
  //   { value: ALL_OPTION, label: "All subscriptions" },
  //   ...subscriptionIds.map(id => ({
  //     value: String(id),
  //     label: `Subscription ${id}`,
  //   })),
  // ];

  const set = (key, value) => onChange({ ...filters, [key]: value });

  return (
    <div className="ws-filters">
      <Select
        data={officeOptions}
        value={filters.officeId}
        onChange={value => set("officeId", value ?? ALL_OPTION)}
        allowDeselect={false}
        radius="xl"
        aria-label="Filter by office"
        styles={filterSelectStyles}
      />
      {/* Subscription filter — the request still sends subscription_id, it just
          stays "all" while this is off.
      <Select
        data={subscriptionOptions}
        value={filters.subscriptionId}
        onChange={value => set("subscriptionId", value ?? ALL_OPTION)}
        allowDeselect={false}
        radius="xl"
        aria-label="Filter by subscription"
        styles={filterSelectStyles}
      />
      */}
    </div>
  );
}

/**
 * Rolled-up welcome-survey figures for the current filters, shown above the
 * report table.
 */
function WelcomeSurveySummary({
  data,
  loading,
  error,
  filters,
  onFiltersChange,
}) {
  const completion = Math.max(
    0,
    Math.min(100, Number(data?.overall_completion_percentage) || 0)
  );

  const stats = [
    {
      label: "Retention of completers",
      value: formatPercent(data?.retention_of_survey_completed_percentage),
      note: `${formatCount(data?.retention_of_survey_completed_count)} of ${formatCount(data?.retention_of_survey_completed_total)}`,
    },
    {
      label: "ROR (cancel ≤ 3 days)",
      value: formatPercent(data?.ror_percentage),
      note: `${formatCount(data?.ror_count)} of ${formatCount(data?.ror_total)}`,
    },
    {
      label: "Cancelled after survey",
      value: formatCount(data?.cancelled_after_survey_count),
      note: `of ${formatCount(data?.cancelled_after_survey_total)} completed`,
    },
    {
      label: "No-survey cancellation %",
      value: formatPercent(data?.no_survey_cancellation_percentage),
      note: `${formatCount(data?.no_survey_cancellation_count)} of ${formatCount(data?.no_survey_cancellation_total)}`,
      alert: Number(data?.no_survey_cancellation_percentage) > 0,
    },
  ];

  return (
    <section className="ws-summary">
      <div
        className={`ws-completion${loading ? " ws-completion-loading" : ""}`}
      >
        <div className="ws-completion-header">
          <h2 className="ws-summary-heading">
            Survey Completion
            {loading && <Loader size="xs" ml="sm" display="inline-block" />}
          </h2>
          {onFiltersChange && (
            <DatePickerInput
              type="range"
              value={filters?.dateRange}
              onChange={value =>
                onFiltersChange({ ...filters, dateRange: value })
              }
              valueFormat="MMM D, YYYY"
              allowSingleDateInRange
              radius="xl"
              aria-label="Reporting window"
              placeholder="Pick dates"
              // Says the pill opens something, the way the button it replaced
              // read as pressable. Pointer events off so the chevron is part of
              // the target rather than a dead spot in it.
              rightSection={<IconChevronDown size={16} />}
              rightSectionPointerEvents="none"
              styles={accentFilterStyles}
            />
          )}
        </div>

        {error ? (
          <div className="ws-summary-error">{error}</div>
        ) : (
          <>
            <div className="ws-completion-figures">
              <div className="ws-completion-percent">
                {Math.round(completion)}%
              </div>
              <div className="ws-completion-count">
                <span className="ws-completion-count-value">
                  {formatCount(data?.new_customers_surveyed)}
                </span>
                <span className="ws-completion-count-total">
                  / {formatCount(data?.new_customers_total)}
                </span>
                <span className="ws-summary-label">New customers surveyed</span>
              </div>
            </div>

            <div className="ws-completion-bar">
              <div
                className="ws-completion-bar-fill"
                style={{ width: `${completion}%` }}
              />
            </div>

            <div className="ws-completion-stats">
              {stats.map(stat => (
                <div key={stat.label}>
                  <div className="ws-summary-label">{stat.label}</div>
                  <div
                    className={`ws-stat-value${stat.alert ? " ws-stat-value-alert" : ""}`}
                  >
                    {stat.value}
                  </div>
                  <div className="ws-stat-note">{stat.note}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <aside className="ws-attention">
        <h2 className="ws-summary-heading ws-attention-heading">
          Needs Your Attention
        </h2>
        <div className="ws-attention-list">
          {attentionItems(data).map(item => (
            <div className="ws-attention-item" key={item.title}>
              <span className="ws-attention-count">{item.count}</span>
              <div>
                <div className="ws-attention-title">{item.title}</div>
                <div className="ws-attention-note">{item.note}</div>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </section>
  );
}

export default WelcomeSurveySummary;
